import PropTypes from 'prop-types';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useIntl } from '@zenbusiness/application-commons-intl';
import Chip from '@zenbusiness/zui/Chip';
import Link from '@zenbusiness/zui/Link';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import ProductBenefits from './ProductBenefits';
import * as Styled from './SubscriptionDetails.style';

// Domain names arrive bare (example.com); public URLs already carry a protocol.
const toExternalHref = (value) => (/^https?:\/\//i.test(value) ? value : `https://${value}`);

function SubscriptionDetails({
  serviceName,
  summary,
  benefits,
  included,
  inTrial,
  activationPath,
  activationLinkText,
  partnerDomain,
  partnerWebsite,
}) {
  const { formatMessage } = useIntl();
  const trackActivationLink = useClickstreamCallback('linkPressed', {
    elementName: 'subscription_activation_link',
    elementText: activationLinkText,
  });

  const formattedLinkText = activationLinkText ? formatMessage({ id: activationLinkText }) : null;

  return (
    <Stack gap={0.5}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Text size="lg" weight="bold">
          {serviceName}
        </Text>
        {inTrial && (
          <Chip
            label={formatMessage({ id: 'dashboard.subscriptions.details.freeTrial' })}
            size="medium"
            color="success"
          />
        )}
      </Stack>
      {summary && (
        <Text size="sm" color="secondary">
          {summary}
        </Text>
      )}
      {partnerDomain?.domainName && (
        <Stack direction="row">
          <Styled.PartnerLinkChip
            label={partnerDomain.domainName}
            size="sm"
            clickable
            component={Link}
            href={toExternalHref(partnerDomain.domainName)}
            target="_blank"
            rel="noopener noreferrer"
            clickstreamProps={{ elementName: 'subscription_partner_domain_link' }}
          />
        </Stack>
      )}
      {partnerWebsite?.publicUrl && (
        <Stack direction="row">
          <Styled.PartnerLinkChip
            label={partnerWebsite.publicUrl}
            size="sm"
            clickable
            component={Link}
            href={toExternalHref(partnerWebsite.publicUrl)}
            target="_blank"
            rel="noopener noreferrer"
            clickstreamProps={{ elementName: 'subscription_partner_website_link' }}
          />
        </Stack>
      )}
      {!included && <ProductBenefits benefits={benefits} />}
      {activationPath && formattedLinkText && (
        <Styled.ActivationLink href={activationPath} underline="none" onClick={trackActivationLink}>
          {formattedLinkText}
          {' →'}
        </Styled.ActivationLink>
      )}
    </Stack>
  );
}

SubscriptionDetails.propTypes = {
  serviceName: PropTypes.string.isRequired,
  summary: PropTypes.string,
  benefits: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
    }),
  ),
  included: PropTypes.bool,
  inTrial: PropTypes.bool,
  activationPath: PropTypes.string,
  activationLinkText: PropTypes.string,
  partnerDomain: PropTypes.shape({
    domainName: PropTypes.string,
  }),
  partnerWebsite: PropTypes.shape({
    publicUrl: PropTypes.string,
  }),
};

export default SubscriptionDetails;
